import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import { supabase } from "../../lib/supabaseClient";
import { useUserProfile } from "../../contexts/UserProfileContext";

type Row = {
  id: string;
  slug: string;
  title: string;
  snippet: string;
  tags: string[] | null;
  created_at: string;
};

export default function AdminFAQList() {
  const { profile } = useUserProfile();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const qParam = searchParams.get("q") ?? "";
  const [q, setQ] = useState(qParam);
  const [rows, setRows] = useState<Row[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!profile || profile.role !== "admin") return;
    const load = async () => {
      setLoading(true);
      setError(null);
      const { data, error } = await supabase.rpc("faq_search", {
        p_q: qParam,
        p_limit: 200,
        p_offset: 0,
      });
      if (error) {
        console.error("faq_search failed:", error.message);
        setError("Konnte FAQ nicht laden.");
        setRows([]);
      } else {
        setRows((data ?? []) as Row[]);
      }
      setLoading(false);
    };
    load();
  }, [profile?.role, qParam]);

  const onSearch = (e: React.FormEvent) => {
    e.preventDefault();
    const next = new URLSearchParams(searchParams);
    if (q.trim()) next.set("q", q.trim());
    else next.delete("q");
    setSearchParams(next, { replace: true });
  };

  const allTags = useMemo(() => {
    const s = new Set<string>();
    rows.forEach((r) => (r.tags ?? []).forEach((t) => t && s.add(t)));
    return Array.from(s).sort((a, b) => a.localeCompare(b));
  }, [rows]);

  if (!profile || profile.role !== "admin") {
    return <div className="text-center text-red-400 mt-10">Kein Zugriff – nur für Admins 🛑</div>;
  }

  return (
    <div className="max-w-5xl mx-auto mt-10 p-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-6">
        <div>
          <h1 className="text-2xl font-bold text-sky-400">📚 FAQ verwalten</h1>
          <p className="text-sm text-slate-400">Einträge suchen, erstellen und bearbeiten.</p>
        </div>
        <button
          onClick={() => navigate("/admin/faq/new")}
          className="bg-sky-500 text-white px-4 py-2 rounded-lg hover:bg-sky-600 transition-colors"
        >
          + Neuer Eintrag
        </button>
      </div>

      <form onSubmit={onSearch} className="flex gap-2 mb-4">
        <input
          className="flex-1 border border-[#2a3347] bg-[#0d1117] text-slate-100 rounded-lg px-3 py-2 outline-none focus:border-sky-400 placeholder:text-slate-600"
          placeholder="Suchen (Titel, Inhalt oder Tags)…"
          value={q}
          onChange={(e) => setQ(e.target.value)}
        />
        <button type="submit" className="px-4 py-2 rounded-lg border border-[#2a3347] text-slate-300 hover:bg-[#1e2639] transition-colors">Suchen</button>
      </form>

      {allTags.length > 0 && (
        <div className="flex flex-wrap gap-2 mb-6">
          {allTags.map((t) => (
            <span key={t} className="px-2 py-0.5 rounded-full text-xs border border-[#2a3347] bg-[#1e2639] text-slate-400">
              #{t}
            </span>
          ))}
        </div>
      )}

      {loading ? (
        <p className="text-slate-400">⏳ Lade Einträge…</p>
      ) : error ? (
        <p className="text-red-400">{error}</p>
      ) : rows.length === 0 ? (
        <p className="text-slate-500">Keine Einträge gefunden.</p>
      ) : (
        <ul className="space-y-3">
          {rows.map((r) => (
            <li key={r.id} className="bg-[#161b27] rounded-xl border border-[#2a3347] p-4 hover:border-sky-500/20 transition-colors">
              <div className="flex items-center justify-between gap-2">
                <div>
                  <h2 className="text-lg font-semibold text-slate-100">{r.title}</h2>
                  <p className="text-sm text-slate-400 mt-1">{r.snippet}…</p>
                  {(r.tags ?? []).length > 0 && (
                    <div className="flex flex-wrap gap-2 mt-2">
                      {(r.tags ?? []).map((t) => (
                        <span key={t} className="px-2 py-0.5 rounded-full text-xs border border-[#2a3347] bg-[#1e2639] text-slate-400">
                          #{t}
                        </span>
                      ))}
                    </div>
                  )}
                </div>
                <div className="flex items-center gap-2">
                  <Link
                    to={`/faq/${r.slug}`}
                    className="text-sm text-sky-400 hover:underline"
                    title="Öffentliche Ansicht"
                  >
                    ansehen
                  </Link>
                  <Link
                    to={`/admin/faq/${r.id}`}
                    className="text-sm text-sky-400 hover:underline"
                    title="Bearbeiten"
                  >
                    bearbeiten
                  </Link>
                </div>
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
